/**
 * Glue between scene store and IndexedDB.
 *
 * Boot order (called from main.tsx):
 *   1. await hydrate()       — pull most-recently-edited design into the store
 *   2. startPersist()        — subscribe; debounced writes from now on
 *
 * Subscribing AFTER hydrate avoids a redundant "save" of the freshly-
 * loaded scene back to itself.
 *
 * Multi-design model (C1):
 *   - Each design lives as its own row in IDB's `designs` store, keyed
 *     by id, with a user-facing `name` field (e.g. "Spaceship").
 *   - The scene store's `currentDesignId` says which row we're editing.
 *     Mutations are debounced and written to that row.
 *   - newDesign() creates a fresh row and switches to it.
 *   - switchDesign(id) flushes the current row, loads the target, and
 *     swaps the in-memory scene.
 *   - On boot, hydrate() loads the most-recently-updated design (via
 *     `updatedAt` desc) — picking up exactly where the kid left off,
 *     across slots. If no designs exist, hydrate seeds the legacy
 *     `default` slot so the canvas opens empty-but-named.
 */

import { useScene } from '../state/scene.ts';
import type { SceneObject } from '../types.ts';
import {
  type DesignRecord,
  deleteDesign as idbDelete,
  renameDesign as idbRename,
  listDesigns,
  loadDesign,
  saveDesign,
} from './indexeddb.ts';

const DEBOUNCE_MS = 500;

interface SerializedScene {
  objects: Record<string, SceneObject>;
  order: string[];
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;

const newDesignId = (): string => `design-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

function readScene(): SerializedScene {
  const s = useScene.getState();
  return { objects: s.objects, order: s.order };
}

function emptyScene(): SerializedScene {
  return { objects: {}, order: [] };
}

function applyRecord(record: DesignRecord): void {
  let parsed: SerializedScene;
  try {
    parsed = JSON.parse(record.sceneJson) as SerializedScene;
  } catch {
    parsed = emptyScene();
  }
  useScene.setState({
    objects: parsed.objects,
    order: parsed.order,
    selectedId: null,
    history: [],
    redoStack: [],
    saveStatus: 'saved',
    currentDesignId: record.id,
    currentDesignName: record.name,
  });
}

export async function hydrate(): Promise<void> {
  const all = await listDesigns();
  const latest = all[0]; // listDesigns returns updatedAt desc
  if (latest) {
    applyRecord(latest);
    return;
  }
  // First-ever boot: seed an empty "default" record so the persist subscriber
  // has somewhere stable to write. Doing it here (rather than waiting for the
  // first edit) keeps the Designs panel non-empty from the start.
  const now = Date.now();
  const seed: DesignRecord = {
    id: 'default',
    name: 'My design',
    sceneJson: JSON.stringify(emptyScene()),
    createdAt: now,
    updatedAt: now,
    syncState: 'local-only',
  };
  await saveDesign(seed);
  useScene.setState({
    currentDesignId: seed.id,
    currentDesignName: seed.name,
  });
}

export function startPersist(): void {
  if (unsubscribe) return;

  let lastObjects = useScene.getState().objects;
  let lastOrder = useScene.getState().order;

  unsubscribe = useScene.subscribe((state) => {
    if (state.objects === lastObjects && state.order === lastOrder) return;
    lastObjects = state.objects;
    lastOrder = state.order;

    useScene.getState().setSaveStatus('saving');

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      void flushNow().catch(() => {
        // Save failed (quota? IDB unavailable?). Leave indicator at 'saving';
        // a follow-on commit (MKO-121 offline detection) will turn this into
        // a visible error state. For now, swallow rather than crash the canvas.
      });
    }, DEBOUNCE_MS);
  });
}

/** Force-flush the pending save (used by tests + design switching). */
export async function flushNow(): Promise<void> {
  const s = useScene.getState();
  const id = s.currentDesignId;
  const existing = await loadDesign(id);
  const now = Date.now();
  const record: DesignRecord = {
    id,
    name: s.currentDesignName,
    sceneJson: JSON.stringify(readScene() satisfies SerializedScene),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    syncState: existing?.syncState ?? 'local-only',
  };
  await saveDesign(record);
  useScene.getState().setSaveStatus('saved');
}

/**
 * Create a new empty design slot, save it, and switch to it. The
 * previous design has already been auto-saved (the persist subscriber
 * writes it on every edit), so nothing is lost.
 */
export async function newDesign(name = 'New design'): Promise<DesignRecord> {
  // Flush whatever's currently being edited so it's durable before we swap.
  await flushNow().catch(() => {});

  const now = Date.now();
  const record: DesignRecord = {
    id: newDesignId(),
    name,
    sceneJson: JSON.stringify(emptyScene()),
    createdAt: now,
    updatedAt: now,
    syncState: 'local-only',
  };
  await saveDesign(record);
  applyRecord(record);
  return record;
}

/** Switch to an existing design slot. Flushes current state first. */
export async function switchDesign(id: string): Promise<void> {
  await flushNow().catch(() => {});
  const target = await loadDesign(id);
  if (!target) throw new Error(`switchDesign: no design with id=${id}`);
  applyRecord(target);
}

/** Rename the currently-active design. */
export async function renameCurrentDesign(name: string): Promise<void> {
  const id = useScene.getState().currentDesignId;
  await idbRename(id, name);
  useScene.setState({ currentDesignName: name });
}

/**
 * Delete a design. If it's the currently-active one, switch to the most-
 * recent remaining one (or seed a fresh "default" if none).
 */
export async function deleteDesign(id: string): Promise<void> {
  await idbDelete(id);
  const current = useScene.getState().currentDesignId;
  if (current !== id) return;

  const all = await listDesigns();
  if (all[0]) {
    applyRecord(all[0]);
    return;
  }
  // Nothing left — make a fresh slot.
  await newDesign('My design');
}

/** Snapshot list for the Designs panel UI. */
export async function getAllDesigns(): Promise<DesignRecord[]> {
  return listDesigns();
}

/** Reset state for tests. */
export function _resetPersistForTests(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
}
